import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [hoveredMenu, setHoveredMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb'
    },
    backdrop: {
      position: 'fixed' as const,
      inset: '0',
      zIndex: 40,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: sidebarOpen && !isDesktop ? 'block' : 'none'
    },
    mainContent: {
      paddingLeft: isDesktop ? '320px' : '0',
      transition: 'padding-left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
    },
    menuButton: {
      position: 'fixed' as const,
      top: '16px',
      left: '16px',
      zIndex: 50,
      display: isDesktop ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      border: 'none',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      cursor: 'pointer',
      transition: 'all 200ms'
    },
    menuButtonHover: {
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      transform: 'scale(1.05)'
    },
    menuIcon: {
      width: '24px',
      height: '24px',
      color: '#1f2937'
    }
  };

  return (
    <div style={styles.container}>
      {/* Mobile menu button */}
      {!isDesktop && (
        <button
          onClick={handleToggleSidebar}
          onMouseEnter={() => setHoveredMenu(true)}
          onMouseLeave={() => setHoveredMenu(false)}
          style={{
            ...styles.menuButton,
            ...(hoveredMenu ? styles.menuButtonHover : {})
          }}
          aria-label="Toggle sidebar"
        >
          <svg
            style={styles.menuIcon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && !isDesktop && (
        <div
          style={styles.backdrop}
          onClick={handleCloseSidebar}
          onKeyDown={(e) => e.key === 'Escape' && handleCloseSidebar()}
          tabIndex={0}
          role="button"
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onCloseSidebar={handleCloseSidebar} 
      />

      {/* Main content */}
      <div style={styles.mainContent}>
        {/* Page content */}
        <main>{children}</main>
      </div>
    </div>
  );
};

export default Layout;
